// Phase 10 — Admin Broadcast (network announcement + DM blast)
import React, { useState, useEffect, useCallback } from "react";
import { Megaphone, Send, Loader2, History } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const AUDIENCES = [
  { value: "all",              label: "All users" },
  { value: "role:affiliate",   label: "All affiliates" },
  { value: "role:driver",      label: "All drivers" },
  { value: "role:partner",     label: "All partners" },
  { value: "role:franchisee",  label: "All franchisees" },
  { value: "role:customer",    label: "All customers" },
];

export default function AdminBroadcast() {
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [mode, setMode] = useState("announcement"); // announcement | dm
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);
  const [audit, setAudit] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const [{ data: a }, { data: b }] = await Promise.all([
        api.get("/admin/messages/audit", { params: { limit: 20 } }),
        api.get("/network/activity", { params: { limit: 30 } }).catch(() => ({ data: { events: [] } })),
      ]);
      setHistory(a.broadcasts || []);
      setAudit((b.events || []).filter((e) => e.event_type === "admin.announcement"));
    } catch (_) {}  // eslint-disable-line no-unused-vars
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const sendDM = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const { data } = await api.post("/admin/messages/broadcast", { body: body.trim(), audience });
      setOk(`Sent to ${data.broadcast.recipient_count} users.`);
      setBody(""); await loadHistory();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const sendAnnouncement = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const roleTarget = audience.startsWith("role:") ? audience.split(":")[1] : "all";
      await api.post("/admin/network/announcement", { title: title.trim(), body: body.trim(), role_target: roleTarget });
      setOk("Announcement posted to network feed.");
      setBody(""); setTitle(""); await loadHistory();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const submit = () => mode === "dm" ? sendDM() : sendAnnouncement();

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" data-testid="admin-broadcast">
      <header>
        <div className="eyebrow">Admin · Communications</div>
        <h1 className="font-anton text-3xl uppercase">Broadcast</h1>
      </header>

      <div className="glass-panel p-4 space-y-3">
        <div className="flex gap-1">
          {["announcement", "dm"].map((m) => (
            <button key={m} onClick={() => setMode(m)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-bebas tracking-[0.18em] uppercase ${
                      mode === m ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40" : "border-white/10 text-lifted-dim"
                    }`} data-testid={`broadcast-mode-${m}`}>
              {m === "announcement" ? "Network Announcement" : "Direct Message Blast"}
            </button>
          ))}
        </div>

        <div>
          <label className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-dim uppercase">Audience</label>
          <select value={audience} onChange={(e) => setAudience(e.target.value)}
                  className="w-full mt-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm"
                  data-testid="broadcast-audience">
            {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>

        {mode === "announcement" && (
          <div>
            <label className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-dim uppercase">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
                   className="w-full mt-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-lifted-green/40"
                   placeholder="e.g. Same-day rolled out to Brooklyn"
                   data-testid="broadcast-title" />
          </div>
        )}

        <div>
          <label className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-dim uppercase">Message body</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4}
                    className="w-full mt-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-lifted-green/40"
                    placeholder="Write your message…" data-testid="broadcast-body" />
        </div>

        {ok && <div className="text-lifted-green text-xs" data-testid="broadcast-ok">{ok}</div>}
        {err && <div className="text-rose-300 text-xs" data-testid="broadcast-err">{err}</div>}

        <button onClick={submit} disabled={!body.trim() || (mode === "announcement" && !title.trim()) || busy}
                className="btn-lifted inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                data-testid="broadcast-send">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Send {mode === "dm" ? "blast" : "announcement"}
        </button>
      </div>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass-panel overflow-hidden" data-testid="broadcast-history">
          <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2">
            <History className="h-3 w-3 text-lifted-green" />
            <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase">Recent DM blasts</div>
          </div>
          {history.length === 0 ? (
            <div className="p-4 text-xs text-lifted-dim">No blasts yet.</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {history.map((b) => (
                <li key={b.id} className="px-3 py-2" data-testid={`broadcast-row-${b.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[0.65rem] text-lifted-dim font-mono">{b.audience}</span>
                    <span className="text-[0.6rem] text-lifted-dim">{(b.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-xs text-white truncate">{b.body}</div>
                  <div className="text-[0.6rem] text-lifted-dim mt-0.5">{b.recipient_count} recipients</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="glass-panel overflow-hidden">
          <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2">
            <Megaphone className="h-3 w-3 text-lifted-green" />
            <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase">Recent feed announcements</div>
          </div>
          {audit.length === 0 ? (
            <div className="p-4 text-xs text-lifted-dim">No announcements yet.</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {audit.map((e) => (
                <li key={e.id} className="px-3 py-2" data-testid={`announce-row-${e.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white">{e.title}</span>
                    <span className="text-[0.6rem] text-lifted-dim">{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-[0.65rem] text-lifted-dim truncate">{e.body}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
